import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { Board, BoardPatch, ColumnCategory, ColumnInput } from '@cortex/domain-org-issues';
import { COLUMN_CATEGORIES } from '@cortex/domain-org-issues/model';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Checkbox } from '@cortex/web-design-system/components/ui/Checkbox';
import { Input } from '@cortex/web-design-system/components/ui/Input';
import { Modal } from '@cortex/web-design-system/components/ui/Modal';
import { MultiPicker } from '@cortex/web-design-system/components/ui/MultiPicker';
import { Select } from '@cortex/web-design-system/components/ui/Select';
import { Textarea } from '@cortex/web-design-system/components/ui/Textarea';
import { useOrgBoardMembers, useUpdateOrgBoard } from '../../../hooks/useOrgIssues.js';
import type { OrgIssuePeople } from '../../../lib/api/org-issues.js';
import { moveItem, personOptions } from '../../../lib/org-issues.js';
import { VISIBILITY_KINDS, type VisibilityKind } from './BoardList.js';

/** 編集中の列。既定の列は消せない（ラベルだけ変えられる）。 @graph-connects none */
interface ColumnDraft extends ColumnInput {
  isDefault: boolean;
}

/** @graph-connects none */
interface BoardSettingsModalProps {
  board: Board;
  people: OrgIssuePeople;
  open: boolean;
  onClose: () => void;
}

/**
 * ボードの設定（名前・公開範囲・編集者・列・本文テンプレート）。開けるのは編集者だけ
 * （`permissions.canManage`）で、変えられるかの判定と文言は API が持つ。列を外せるかも
 * API が判定する（課題が残っている列は外せない）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 組織課題ボードの名前・公開範囲・編集者・列（追加・名前変更・並べ替え・削除）・本文テンプレートを変える
 * @graph-connects useUpdateOrgBoard [calls] ボードの設定変更
 * @graph-connects useOrgBoardMembers [calls] 編集者の候補
 */
export function BoardSettingsModal({
  board,
  people,
  open,
  onClose,
}: BoardSettingsModalProps): ReactNode {
  const { t } = useTranslation();
  const update = useUpdateOrgBoard(board.id);
  const members = useOrgBoardMembers(board.id);
  const [name, setName] = useState(board.name);
  const isUsersVisibility = board.visibility.kind === 'users';
  const [visibility, setVisibility] = useState<VisibilityKind | null>(
    board.visibility.kind === 'users' ? null : board.visibility.kind,
  );
  const [includeOwnerLeaders, setIncludeOwnerLeaders] = useState(board.editors.includeOwnerLeaders);
  const [editorIds, setEditorIds] = useState(board.editors.userIds);
  const [bodyTemplate, setBodyTemplate] = useState(board.bodyTemplate);
  const [columns, setColumns] = useState<ColumnDraft[]>(() =>
    [...board.columns]
      .sort((a, b) => a.position - b.position)
      .map(({ id, label, category, isDefault }) => ({ id, label, category, isDefault })),
  );
  const editorCandidates = personOptions({ ...people, ...members.data?.people }, [
    ...new Set([...(members.data?.members ?? []), ...board.editors.userIds]),
  ]);

  const updateColumn = (index: number, change: Partial<ColumnDraft>) => {
    setColumns(columns.map((column, i) => (i === index ? { ...column, ...change } : column)));
  };

  const patch: BoardPatch = {
    name,
    editors: { includeOwnerLeaders, userIds: editorIds },
    columns: columns.map(({ id, label, category }) => ({
      ...(id === undefined ? {} : { id }),
      label,
      category,
    })),
    bodyTemplate,
    ...(visibility === null ? {} : { visibility: { kind: visibility } }),
  };

  return (
    <Modal open={open} onClose={onClose} size="xl" title={t('orgIssues.board.settings')}>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          update.mutate(patch, { onSuccess: onClose });
        }}
      >
        <div className="grid gap-3 md:grid-cols-2">
          <Input
            label={t('orgIssues.settings.name')}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
            }}
          />
          {isUsersVisibility && visibility === null ? (
            <p className="self-end text-sm text-[var(--color-text-muted)]">
              {t('orgIssues.settings.usersVisibility')}
            </p>
          ) : (
            <Select
              label={t('orgIssues.settings.visibility')}
              value={visibility ?? ''}
              options={VISIBILITY_KINDS.map((kind) => ({
                value: kind,
                label: t(`orgIssues.visibility.${kind}`),
              }))}
              onChange={(e) => {
                setVisibility(VISIBILITY_KINDS.find((kind) => kind === e.target.value) ?? null);
              }}
            />
          )}
        </div>
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm text-[var(--color-text-primary)]">
            <Checkbox
              checked={includeOwnerLeaders}
              onChange={(e) => {
                setIncludeOwnerLeaders(e.target.checked);
              }}
            />
            {t('orgIssues.settings.includeOwnerLeaders')}
          </label>
          <MultiPicker
            label={t('orgIssues.settings.editorUsers')}
            options={editorCandidates}
            value={editorIds}
            searchable
            onChange={setEditorIds}
          />
        </div>
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">
            {t('orgIssues.settings.columns')}
          </h3>
          {columns.map((column, index) => (
            <div
              key={column.id ?? `new-${String(index)}`}
              className="grid items-end gap-2 md:grid-cols-[1fr_10rem_auto]"
            >
              <Input
                aria-label={t('orgIssues.settings.columnLabel')}
                placeholder={t('orgIssues.settings.columnLabel')}
                helperText={column.isDefault ? t('orgIssues.settings.defaultColumn') : undefined}
                value={column.label}
                onChange={(e) => {
                  updateColumn(index, { label: e.target.value });
                }}
              />
              <Select
                aria-label={t('orgIssues.settings.columnCategory')}
                value={column.category}
                disabled={column.isDefault}
                options={COLUMN_CATEGORIES.map((category) => ({
                  value: category,
                  label: t(`orgIssues.category.${category}`),
                }))}
                onChange={(e) => {
                  const category = COLUMN_CATEGORIES.find((value) => value === e.target.value);
                  if (category !== undefined) updateColumn(index, { category });
                }}
              />
              <div className="flex gap-1">
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setColumns(moveItem(columns, index, -1));
                  }}
                >
                  {t('orgIssues.actions.up')}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setColumns(moveItem(columns, index, 1));
                  }}
                >
                  {t('orgIssues.actions.down')}
                </Button>
                {!column.isDefault && (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setColumns(columns.filter((_, i) => i !== index));
                    }}
                  >
                    {t('orgIssues.settings.removeColumn')}
                  </Button>
                )}
              </div>
            </div>
          ))}
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={() => {
              const category: ColumnCategory = 'doing';
              setColumns([...columns, { label: '', category, isDefault: false }]);
            }}
          >
            {t('orgIssues.settings.addColumn')}
          </Button>
        </div>
        <label className="block space-y-1 text-sm text-[var(--color-text-secondary)]">
          <span>{t('orgIssues.settings.bodyTemplate')}</span>
          <Textarea
            rows={6}
            value={bodyTemplate}
            onChange={(e) => {
              setBodyTemplate(e.target.value);
            }}
          />
        </label>
        {update.isError && (
          <p className="whitespace-pre-wrap text-sm text-[var(--color-error)]" role="alert">
            {update.error.message}
          </p>
        )}
        <div className="flex gap-2">
          <Button type="submit" size="sm" loading={update.isPending}>
            {t('orgIssues.settings.save')}
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={onClose}>
            {t('orgIssues.common.cancel')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
